import React, { useState, useEffect } from 'react'
import styles from './EmployeeArchive.module.scss'
import { 
  getEmployees,
  getEmployeeCases,
  createEmployeeCase,
  createEmployeeDocument,
  uploadEmployeeDocumentFile,
  updateEmployeeCase,
  deleteEmployeeCase
} from '../../../service/ArchiveService'
import { ScaleLoader } from 'react-spinners'
import Notification from '../../../utils/Notifications'

function EmployeeArchive() {
  const [cases, setCases] = useState([])
  const [employees, setEmployees] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedCase, setSelectedCase] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [showCreateCaseForm, setShowCreateCaseForm] = useState(false)
  const [showDocumentForm, setShowDocumentForm] = useState(false)
  const [selectedEmployee, setSelectedEmployee] = useState(null)
  const [documentData, setDocumentData] = useState({
    employee_case: null,
    document_type: 'employment_order',
    title: '',
    document_date: '',
    pages: 0,
    confidentiality_level: 'internal',
    notes: ''
  })
  const [uploadFile, setUploadFile] = useState(null)
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '', sound: '' })

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [casesData, employeesData] = await Promise.all([
        getEmployeeCases(),
        getEmployees()
      ])
      setCases(casesData)
      setEmployees(employeesData)
    } catch (error) {
      console.error('Ошибка при загрузке данных:', error)
      setNotify({
        isOpen: true,
        message: 'Ошибка при загрузке данных',
        type: 'error',
        sound: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const loadEmployeeCases = async () => {
    try {
      const data = await getEmployeeCases()
      setCases(data)
    } catch (error) {
      console.error('Ошибка при загрузке личных дел сотрудников:', error)
    }
  }

  const handleCreateCase = async (e) => {
    e.preventDefault()
    if (!selectedEmployee) {
      setNotify({
        isOpen: true,
        message: 'Выберите сотрудника',
        type: 'warning',
        sound: 'warning',
      })
      return
    }
    
    try {
      const newCase = await createEmployeeCase({
        employee_id: selectedEmployee.id,
        storage_term: 75,
        status: 'active'
      })
      setCases([...cases, newCase])
      setSelectedEmployee(null)
      setShowCreateCaseForm(false)
      setNotify({
        isOpen: true,
        message: 'Личное дело создано успешно!',
        type: 'success',
        sound: 'success',
      })
    } catch (error) {
      console.error('Ошибка при создании личного дела:', error)
      setNotify({
        isOpen: true,
        message: 'Ошибка при создании личного дела',
        type: 'error',
        sound: 'error',
      })
    }
  }

  const handleAddDocument = async (e) => {
    e.preventDefault()
    try {
      const docData = {
        ...documentData,
        employee_case: selectedCase.id
      }
      // Создаем документ с файлом сразу
      const newDocument = await createEmployeeDocument(docData, uploadFile)
      
      // Обновляем выбранное дело
      const updatedCases = cases.map(c => 
        c.id === selectedCase.id 
          ? { ...c, documents: [...(c.documents || []), newDocument] }
          : c
      )
      setCases(updatedCases)
      setSelectedCase({ ...selectedCase, documents: [...(selectedCase.documents || []), newDocument] })
      
      setDocumentData({
        employee_case: null,
        document_type: 'employment_order',
        title: '',
        document_date: '',
        pages: 0,
        confidentiality_level: 'internal',
        notes: ''
      })
      setUploadFile(null)
      setShowDocumentForm(false)
      setNotify({
        isOpen: true,
        message: 'Документ успешно добавлен!',
        type: 'success',
        sound: 'success',
      })
    } catch (error) {
      console.error('Ошибка при добавлении документа:', error)
      setNotify({
        isOpen: true,
        message: 'Ошибка при добавлении документа',
        type: 'error',
        sound: 'error',
      })
    }
  }

  const handleUpdateCase = async (caseId, updates) => {
    try {
      const updatedCase = await updateEmployeeCase(caseId, updates)
      setCases(cases.map(c => c.id === caseId ? updatedCase : c))
      if (selectedCase?.id === caseId) {
        setSelectedCase(updatedCase)
      }
      setNotify({
        isOpen: true,
        message: 'Дело успешно обновлено!',
        type: 'success',
        sound: 'success',
      })
    } catch (error) {
      console.error('Ошибка при обновлении дела:', error)
      setNotify({
        isOpen: true,
        message: 'Ошибка при обновлении дела',
        type: 'error',
        sound: 'error',
      })
    }
  }

  const handleDeleteCase = async (caseId) => {
    if (!window.confirm('Вы уверены, что хотите удалить это дело?')) {
      return
    }
    try {
      await deleteEmployeeCase(caseId)
      setCases(cases.filter(c => c.id !== caseId))
      if (selectedCase?.id === caseId) {
        setSelectedCase(null)
      }
      setNotify({
        isOpen: true,
        message: 'Дело успешно удалено!',
        type: 'success',
        sound: 'success',
      })
    } catch (error) {
      console.error('Ошибка при удалении дела:', error)
      setNotify({
        isOpen: true,
        message: 'Ошибка при удалении дела',
        type: 'error',
        sound: 'error',
      })
    }
  }

  const filteredCases = cases.filter(
    (caseItem) =>
      caseItem.employee_full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      caseItem.employee_id?.includes(searchQuery) ||
      caseItem.department?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const documentTypes = [
    { value: 'employment_order', label: 'Приказ о приёме' },
    { value: 'labor_contract', label: 'Трудовой договор' },
    { value: 'certification', label: 'Аттестация' },
    { value: 'award', label: 'Награда' },
    { value: 'salary_sheet', label: 'Ведомость з/п' },
    { value: 'vacation_sheet', label: 'Расчётный лист' },
    { value: 'disciplinary_action', label: 'Дисциплинарное взыскание' },
    { value: 'memo', label: 'Служебная записка' },
    { value: 'other', label: 'Прочее' },
  ]

  const getDocumentTypeLabel = (value) => {
    const type = documentTypes.find(t => t.value === value)
    return type ? type.label : value
  }

  const getConfidentialityLabel = (value) => {
    const levels = {
      'public': 'Открытый',
      'internal': 'Внутренний',
      'confidential': 'Конфиденциальный',
      'secret': 'Секретный'
    }
    return levels[value] || value
  }

  // Фильтруем сотрудников, у которых нет дел
  const employeesWithoutCases = employees.filter(emp => {
    return !cases.some(c => c.employee === emp.id)
  })

  if (loading) {
    return (
      <div className={styles.loading_container}>
        <ScaleLoader color="#ff8c00" />
      </div>
    )
  }

  return (
    <>
      <Notification notify={notify} setNotify={setNotify} />
      <div className={styles.employee_archive}>
      <div className={styles.header}>
        <h1>Архив сотрудников</h1>
        <button
          className={styles.btn_create}
          onClick={() => setShowCreateCaseForm(!showCreateCaseForm)}
        >
          + Создать дело
        </button>
      </div>

      {showCreateCaseForm && (
        <div className={styles.modal}>
          <div className={styles.modal_content}>
            <button 
              className={styles.close_btn}
              onClick={() => {
                setShowCreateCaseForm(false)
                setSelectedEmployee(null)
              }}
              type="button"
            >
              ×
            </button>
            <h2>Создание личного дела сотрудника</h2>
            <form onSubmit={handleCreateCase}>
              <div className={styles.form_field}>
                <label>Сотрудник *</label>
                <select
                  value={selectedEmployee?.id || ''}
                  onChange={(e) => {
                    const empId = parseInt(e.target.value)
                    const emp = employeesWithoutCases.find(emp => emp.id === empId)
                    setSelectedEmployee(emp)
                  }}
                  required
                >
                  <option value="">Выберите сотрудника</option>
                  {employeesWithoutCases.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.label || emp.full_name || `${emp.surname || ''} ${emp.first_name || ''}`}
                    </option>
                  ))}
                </select>
              </div>
              {selectedEmployee && (
                <div className={styles.employee_info}>
                  <p><strong>ФИО:</strong> {selectedEmployee.label || selectedEmployee.full_name || `${selectedEmployee.surname || ''} ${selectedEmployee.first_name || ''}`}</p>
                  {selectedEmployee.email && <p><strong>Email:</strong> {selectedEmployee.email}</p>}
                  {selectedEmployee.number_phone && <p><strong>Телефон:</strong> {selectedEmployee.number_phone}</p>}
                </div>
              )}
              <div className={styles.form_actions}>
                <button type="submit" className={styles.btn_save}>
                  Создать
                </button>
                <button
                  type="button"
                  className={styles.btn_cancel}
                  onClick={() => {
                    setShowCreateCaseForm(false)
                    setSelectedEmployee(null)
                  }}
                >
                  Отмена
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className={styles.search_bar}>
        <input
          type="text"
          placeholder="Поиск по ФИО, табельному номеру, отделу..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={styles.search_input}
        />
      </div>

      <div className={styles.content}>
        <div className={styles.cases_container}>
          <h2>Личные дела сотрудников ({filteredCases.length})</h2>
          <div className={styles.cases_grid}>
            {filteredCases.map((caseItem) => (
              <div
                key={caseItem.id}
                className={`${styles.case_card} ${selectedCase?.id === caseItem.id ? styles.active : ''}`}
                onClick={() => setSelectedCase(caseItem)}
              >
                <div className={styles.card_header}>
                  <div className={styles.employee_name}>{caseItem.employee_full_name}</div>
                  <div className={styles.status_badge}>{caseItem.status}</div>
                </div>
                <div className={styles.card_body}>
                  <p>
                    <strong>ФИО:</strong> {caseItem.employee_full_name}
                  </p>
                  <p>
                    <strong>Табельный №:</strong> {caseItem.employee_id}
                  </p>
                  <p>
                    <strong>Отдел:</strong> {caseItem.department}
                  </p>
                  <p>
                    <strong>№ Дела:</strong> {caseItem.case_number}
                  </p>
                  {caseItem.is_digitized && <span className={styles.digitized_badge}>✓ Оцифровано</span>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {selectedCase && (
          <div className={styles.details_panel}>
            <div className={styles.panel_header}>
              <h2>Кадровые документы</h2>
              <button
                className={styles.btn_add_doc}
                onClick={() => setShowDocumentForm(!showDocumentForm)}
              >
                + Добавить документ
              </button>
            </div>

            {showDocumentForm && (
              <div className={styles.document_form}>
                <h3>Добавление документа</h3>
                <form onSubmit={handleAddDocument}>
                  <div className={styles.form_field}>
                    <label>Тип документа *</label>
                    <select
                      value={documentData.document_type}
                      onChange={(e) => setDocumentData({ ...documentData, document_type: e.target.value })}
                      required
                    >
                      {documentTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.form_field}>
                    <label>Название документа *</label>
                    <input
                      type="text"
                      placeholder="Введите название"
                      required
                      value={documentData.title}
                      onChange={(e) => setDocumentData({ ...documentData, title: e.target.value })}
                    />
                  </div>
                  <div className={styles.form_field}>
                    <label>Дата документа *</label>
                    <input
                      type="date"
                      required
                      value={documentData.document_date}
                      onChange={(e) => setDocumentData({ ...documentData, document_date: e.target.value })}
                    />
                  </div>
                  <div className={styles.form_field}>
                    <label>Количество страниц</label>
                    <input
                      type="number"
                      placeholder="0"
                      value={documentData.pages}
                      onChange={(e) => setDocumentData({ ...documentData, pages: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div className={styles.form_field}>
                    <label>Уровень конфиденциальности</label>
                    <select
                      value={documentData.confidentiality_level}
                      onChange={(e) => setDocumentData({ ...documentData, confidentiality_level: e.target.value })}
                    >
                      <option value="public">Открытый</option>
                      <option value="internal">Внутренний</option>
                      <option value="confidential">Конфиденциальный</option>
                      <option value="secret">Секретный</option>
                    </select>
                  </div>
                  <div className={styles.form_field}>
                    <label>Примечания</label>
                    <textarea
                      placeholder="Дополнительная информация"
                      value={documentData.notes}
                      onChange={(e) => setDocumentData({ ...documentData, notes: e.target.value })}
                    />
                  </div>
                  <div className={styles.form_field}>
                    <label>Файл документа</label>
                    <input
                      type="file"
                      onChange={(e) => setUploadFile(e.target.files[0])}
                    />
                  </div>
                  <div className={styles.form_actions}>
                    <button type="submit" className={styles.btn_save}>
                      Добавить
                    </button>
                    <button
                      type="button"
                      className={styles.btn_cancel}
                      onClick={() => setShowDocumentForm(false)}
                    >
                      Отмена
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className={styles.case_info}>
              <p>
                <strong>Сотрудник:</strong> {selectedCase.employee_full_name}
              </p>
              <p>
                <strong>Дело:</strong> {selectedCase.case_number}
              </p>
              <p>
                <strong>Срок хранения:</strong> {selectedCase.storage_term} лет
              </p>
              <div className={styles.case_actions}>
                <button
                  className={styles.btn_digitize}
                  onClick={() => handleUpdateCase(selectedCase.id, { is_digitized: !selectedCase.is_digitized })}
                >
                  {selectedCase.is_digitized ? '✓ Оцифровано' : 'Оцифровать'}
                </button>
                <button
                  className={styles.btn_delete}
                  onClick={() => handleDeleteCase(selectedCase.id)}
                >
                  Удалить дело
                </button>
              </div>
            </div>

            <div className={styles.documents}>
              {selectedCase.documents && selectedCase.documents.length > 0 ? (
                <div className={styles.documents_list}>
                  {selectedCase.documents.map((doc) => (
                    <div key={doc.id} className={styles.document_item}>
                      <div className={styles.doc_type}>{getDocumentTypeLabel(doc.document_type)}</div>
                      <div className={styles.doc_info}>
                        <p className={styles.doc_title}>{doc.title}</p>
                        <small>Дата: {new Date(doc.document_date).toLocaleDateString('ru-RU')}</small>
                        {doc.pages > 0 && <small> • Страниц: {doc.pages}</small>}
                        {doc.confidentiality_level && (
                          <small> • {getConfidentialityLabel(doc.confidentiality_level)}</small>
                        )}
                        {doc.files?.length > 0 && (
                          <div className={styles.doc_files}>
                            {doc.files.map((file) => (
                              <div key={file.id} className={styles.file_item}>
                                <a href={file.file} download className={styles.file_link}>
                                  📄 {file.file_name}
                                </a>
                                <a 
                                  href={file.file} 
                                  download={file.file_name}
                                  className={styles.btn_download}
                                  title="Скачать файл"
                                >
                                  ⬇️ Скачать
                                </a>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className={styles.no_documents}>Документов нет</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  )
}

export default EmployeeArchive
